
import { throwError as observableThrowError, Observable, Subject, of } from 'rxjs';
import { take, map, flatMap, catchError } from 'rxjs/operators';
import { stringify } from 'querystring';
import { DashboardEventService } from './../dashboard/dashboard-event.service';
import { PollingStatus } from './polling-status';
import { CognitoUtil, Callback } from './../authentication/cognito.service';
import { Injectable, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RegistryService } from './registry-service';
import { ServiceRegistry } from './service-registry.service';
import { AuthenticationError } from '../authentication/authentication-error';
import { TenantInfo } from '../tenant/tenant-info';
import * as zlib from 'zlib';
import { AwsUtil } from '../authentication/aws.service';
import { WebApiEndpoints } from '@liquitics/transaction';

@Injectable()
export abstract class BaseRestService<T> implements RegistryService {
    protected dataCache: any;
    protected duringRequest = false;
    protected tenantInfo: TenantInfo;

    // private backoffDelay = 1000;


    constructor(protected http: HttpClient,
        protected awsUtil: AwsUtil,
        protected serviceRegistry: ServiceRegistry,
        protected dashboardEventService: DashboardEventService) {
        this.init();
    }

    abstract getAPIName(params?: any): string;

    protected init() {
        this.serviceRegistry.registerService(this);
    }

    setTenantInfo(info: TenantInfo) {
        this.tenantInfo = info;
    }

    useDemoDataFile(): boolean {
        return false;
    }

    postResolveData(data: T, params?: any): T {
        return data;
    }

    getDataObservable(params?: any): Observable<T> {
        if (this.useDemoDataFile() && this.dataCache) {
            const obs = new Observable<T>((observer) => {
                observer.next(this.dataCache);
            });
            return obs;
        }

        const observable = this.observableWithIdToken(params);
        return observable;
    }


    protected requestHeaders(token: any): HttpHeaders {
        return this.awsUtil.webAPIRequestHeaders(token);
    }

    private pushAuthenticationError() {
        this.dashboardEventService.emergencyMessageSubject.next({
            severity: 'error',
            summary: 'Authentication Error',
            detail: 'Invalid session. Please re-login'
        });
    }

    protected observableWithIdToken(params?: any): Observable<T> {
        return this.idTokenObservable()
            .pipe(flatMap((token) => {
                if (!token) {
                    // No token received back. User Session is invalid, token could not be refreshed, authentication failed, etc..
                    this.serviceRegistry.pollingProblemOccurred();

                    // Let's see if this is just a (1) network connection problem or (2)something wrong with authentication
                    // If it's #2, we need to ask the user to re-login
                    const user = this.awsUtil.cognitoUtil.getCurrentUser();
                    if (!user) {
                        this.pushAuthenticationError();
                    } else {
                        user.getSession((err, session) => {
                            if (err) {
                                console.log(err);
                                this.pushAuthenticationError();
                            } else {
                                if (!session.isValid()) {
                                    this.pushAuthenticationError();
                                }
                            }
                        });
                    }
                    this.duringRequest = false;
                    return of({} as T);
                }
                const obs: Observable<T> = this.makeAPICall(token, params)
                    .pipe(map((payload: any) => {
                        return this.unwrapAPIPayload(payload);
                    }))
                    .pipe(map((res: T) => {
                        try {
                            return this.convertResponse(res);
                        } catch (e) {
                            console.log('Error parsing JSON for result: ' + res + '. Exception:' + e, e);
                        } finally {
                            this.serviceRegistry.pollingSuccessful();
                            this.duringRequest = false;
                        }
                    }))
                    .pipe(map((data: T) => {
                        const returnData: T = this.postResolveData(data, params);
                        if (this.useDemoDataFile()) {
                            this.dataCache = returnData;
                        }
                        return returnData;
                    }))
                    .pipe(catchError((err, caught) => {
                        this.duringRequest = false;
                        return observableThrowError(this.handleServiceCallError(err));
                    }))
                    ;
                // .retryWhen((errors) => {
                //     this.backoffDelay = Math.min(this.backoffDelay + 1000, 10000);
                //     return errors.delay(this.backoffDelay);
                // })

                return obs;
            }))
            ;
    }

    protected handleServiceCallError(err: any): Error {
        console.log('Error during REST call:' + err, err);
        return new Error(err);
    }

    protected makeAPICall(authToken: string, params?: any): Observable<T> {
        const options = { headers: this.requestHeaders(authToken) };
        // const tenantName = 'LiquiticsBank-';
        // const functionName = tenantName + this.getAPIName(params);
        // const invokeParam = {
        //     FunctionName: functionName,
        // } as AWS.Lambda.Types.InvocationRequest;
        // if (params) {
        //     invokeParam.Payload = JSON.stringify(params);
        // }
        // const promise = this.awsUtil.lambda(invokeParam)
        //     .then((response: AWS.Lambda.Types.InvocationResponse) => {
        //         const payload = response.Payload;
        //         return this.unwrapAPIPayload(payload);
        //     });

        // return Observable.fromPromise(promise);
        let url = this.tenantInfo.apiUrl + this.getAPIName(params);
        // if (this.getAPIName(params) === WebApiEndpoints.QueryApplicationConfigurations) {
        //     url = `https://14t4dmo729.execute-api.us-west-2.amazonaws.com/Stage/${this.getAPIName(params)}`;
        // }
        const queryString = stringify(params);
        // return this.http.get<T>(url + '?' + queryString, options);
        return new Observable((observer) => {
            this.awsUtil.webAPICall(this.getAPIName(params), params)
            .then((value: T) => {
                observer.next(value);
            })

        });
        // return new Observable.awsUtil.webAPICall(url, params);
    }

    protected unwrapAPIPayload(payload: any) {
        return payload;
    }

    protected convertResponse(res: any): T {
        return res;
    }

    idTokenObservable(): Observable<string> {
        return new Observable((observer) => {
            this.awsUtil.cognitoUtil.getAccessToken()
            .then((token: string) => {
                observer.next(token);
            });
        });
    }
}

